import React, { useCallback } from 'react';
import { Table, Button, Loader, Alert, Text, Badge } from '@mantine/core';
import {
    IconCirclePlus,
    IconDotsVertical,
    IconPencil,
    IconTrash,
} from '@tabler/icons-react';
import { GET_TAX_CODE_CONFIGS } from '../../apollo/queries/vatConfig';
import { DELETE_TAX_CODE_CONFIG } from '../../apollo/mutations/vatConfig';
import { ConfirmDeleteModal, TableActions } from '../common';
import { TaxCodeConfigFormModal } from './TaxCodeConfigFormModal';
import { TaxCodeConfigRow } from './vatConfigTypes';
import { useVatConfigCrud } from './useVatConfigCrud';

type TaxCodeConfigsQueryData = {
    taxCodeConfigs: TaxCodeConfigRow[];
};

export function TaxCodeConfigManager() {
    const getRows = useCallback(
        (data: TaxCodeConfigsQueryData | undefined) =>
            data?.taxCodeConfigs ?? [],
        [],
    );

    const getDeleteSuccessCopy = useCallback(
        (row: TaxCodeConfigRow) => ({
            title: 'Tax code deleted',
            message: `${row.code} has been removed.`,
        }),
        [],
    );

    const {
        rows: configs,
        loading,
        error,
        deleting,
        selectedConfig,
        formModalOpened,
        formModalHandlers,
        deleteModalOpened,
        deleteModalHandlers,
        handleAdd,
        handleEdit,
        handleDeleteRequest,
        handleDeleteConfirm,
    } = useVatConfigCrud<TaxCodeConfigRow, TaxCodeConfigsQueryData>({
        query: GET_TAX_CODE_CONFIGS,
        deleteMutation: DELETE_TAX_CODE_CONFIG,
        getRows,
        getDeleteSuccessCopy,
        deleteErrorMessage: 'Failed to delete tax code',
    });

    if (loading) {
        return (
            <div className="flex justify-center p-6">
                <Loader size="sm" />
            </div>
        );
    }

    if (error) {
        return (
            <Alert color="red" title="Error loading tax codes">
                {error.message}
            </Alert>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <Text size="sm" c="dimmed">
                    Maps SAP MWSKZ tax codes from IDOC imports to VAT treatment.
                    Zero-rated codes always override the country/global VAT
                    rate.
                </Text>
                <Button
                    type="button"
                    size="xs"
                    rightSection={<IconCirclePlus size={16} />}
                    onClick={handleAdd}
                >
                    Add Tax Code
                </Button>
            </div>

            <Table withTableBorder withColumnBorders striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Code</Table.Th>
                        <Table.Th>Description</Table.Th>
                        <Table.Th>Treatment</Table.Th>
                        <Table.Th w={60}>Actions</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {configs.length === 0 ? (
                        <Table.Tr>
                            <Table.Td colSpan={4}>
                                <Text size="sm" c="dimmed" ta="center" py="md">
                                    No tax codes configured yet.
                                </Text>
                            </Table.Td>
                        </Table.Tr>
                    ) : (
                        configs.map(config => (
                            <Table.Tr key={config.id}>
                                <Table.Td>{config.code}</Table.Td>
                                <Table.Td>{config.description}</Table.Td>
                                <Table.Td>
                                    {config.isZeroRated ? (
                                        <Badge color="teal" variant="light">
                                            Zero-rated
                                        </Badge>
                                    ) : (
                                        <Badge color="gray" variant="light">
                                            Standard rated
                                        </Badge>
                                    )}
                                </Table.Td>
                                <Table.Td>
                                    {deleting &&
                                    selectedConfig?.id === config.id ? (
                                        <Loader size="xs" />
                                    ) : (
                                        <TableActions
                                            row={config}
                                            options={[
                                                {
                                                    label: 'Edit',
                                                    icon: (
                                                        <IconPencil
                                                            className="text-primary"
                                                            size={16}
                                                        />
                                                    ),
                                                    onClick: () =>
                                                        handleEdit(config),
                                                    isShown: true,
                                                },
                                                {
                                                    label: 'Delete',
                                                    icon: (
                                                        <IconTrash
                                                            color="red"
                                                            size={16}
                                                        />
                                                    ),
                                                    onClick: () =>
                                                        handleDeleteRequest(
                                                            config,
                                                        ),
                                                    isShown: true,
                                                },
                                            ]}
                                        >
                                            <Button
                                                type="button"
                                                variant="subtle"
                                                className="text-primary rounded-md p-1 w-min h-min"
                                            >
                                                <IconDotsVertical size={16} />
                                            </Button>
                                        </TableActions>
                                    )}
                                </Table.Td>
                            </Table.Tr>
                        ))
                    )}
                </Table.Tbody>
            </Table>

            <TaxCodeConfigFormModal
                opened={formModalOpened}
                onClose={formModalHandlers.close}
                taxCodeConfig={selectedConfig}
            />
            <ConfirmDeleteModal
                opened={deleteModalOpened}
                close={deleteModalHandlers.close}
                onConfirm={handleDeleteConfirm}
                subject="Tax Code"
                message={
                    selectedConfig
                        ? `Are you sure you want to delete the tax code ${selectedConfig.code}?`
                        : undefined
                }
                loading={deleting}
            />
        </div>
    );
}
